// NotificationsPage - Main notifications page with infinite scroll
// Feature: 015-we-want-to
// Simplified page: no filters, infinite scroll, immediate mark-as-read

import { useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useInfiniteNotifications } from '../hooks/useInfiniteNotifications';
import { useMarkNotificationsRead } from '../hooks/useMarkNotificationsRead';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import { useAuthStore } from '../store/authStore';
import { NotificationType, type UserNotification } from '../types/notifications';
import { DefaultPageLayout } from '../ui/layouts/DefaultPageLayout';
import { formatDistanceToNow } from 'date-fns';

const ITEMS_PER_PAGE = 20;

/**
 * Convert IPFS URLs to HTTP gateway URLs for display
 * Handles ipfs:// protocol URLs by converting them to a public gateway
 */
function convertImageUrl(url: string | null | undefined): string | null {
  if (!url) return null;

  // If it's an IPFS URL, convert to HTTP gateway
  if (url.startsWith('ipfs://')) {
    const hash = url.replace('ipfs://', '');
    return `https://ipfs.io/ipfs/${hash}`;
  }

  return url;
}

export function NotificationsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const hasMarkedAsRead = useRef(false);
  const { isAuthenticated } = useAuthStore();

  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useInfiniteNotifications({ limit: ITEMS_PER_PAGE });

  const markAsReadMutation = useMarkNotificationsRead();

  // Redirect to home if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  // Flatten all notifications from all pages
  const notifications = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => page.notifications);
  }, [data?.pages]);

  const totalCount = data?.pages[0]?.pagination.total ?? 0;

  // Immediately mark all as read when page loads (only once)
  useEffect(() => {
    if (!hasMarkedAsRead.current && notifications.length > 0) {
      hasMarkedAsRead.current = true;
      markAsReadMutation.mutate(undefined, {
        onSuccess: () => {
          // Invalidate unread count to update the badge
          queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
        },
      });
    }
  }, [notifications.length, markAsReadMutation, queryClient]);

  // Infinite scroll handler
  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const sentinelRef = useInfiniteScroll({
    onLoadMore: handleLoadMore,
    enabled: hasNextPage && !isFetchingNextPage,
    rootMargin: '300px',
  });

  const handleNotificationClick = (notification: UserNotification) => {
    // Minter revoked - navigate to launch dashboard
    if (notification.type === NotificationType.LAUNCHPAD_MINTER_REVOKED) {
      const launchId = (notification.data as any).launchId;
      if (launchId) {
        navigate(`/launchpad/${launchId}`);
        return;
      }
    }
    // Rewards notification - navigate to project page
    if (notification.type === NotificationType.REWARDS_CLAIM_PERIOD_OPENED) {
      const slug = (notification.data as any).projectSlug;
      if (slug) {
        navigate(`/projects/${slug}?tab=rewards`);
        return;
      }
    }
    const nftTokenId = notification.data.nftTokenId;
    if (nftTokenId) {
      navigate(`/nfts/${nftTokenId}`);
    }
  };

  const formatPrice = (price: number) => {
    return `${price.toLocaleString()} XRP`;
  };

  const getNotificationTypeBadge = (type: NotificationType) => {
    switch (type) {
      case NotificationType.NFT_RECEIVED_OFFER:
        return { text: 'New Offer', color: 'bg-blue-100 text-blue-800' };
      case NotificationType.NFT_BOUGHT:
        return { text: 'Bought', color: 'bg-green-100 text-green-800' };
      case NotificationType.NFT_SOLD:
        return { text: 'Sold', color: 'bg-purple-100 text-purple-800' };
      case NotificationType.NFT_OFFER_ACCEPTED:
        return { text: 'Offer Accepted', color: 'bg-green-100 text-green-800' };
      case NotificationType.NFT_TRANSFER_OFFER_RECEIVED:
        return { text: 'Transfer', color: 'bg-indigo-100 text-indigo-800' };
      case NotificationType.COLLECTION_NEW_LISTING:
        return { text: 'New Listing', color: 'bg-yellow-100 text-yellow-800' };
      case NotificationType.COLLECTION_NEW_FLOOR:
        return { text: 'Floor Update', color: 'bg-orange-100 text-orange-800' };
      case NotificationType.COLLECTION_SALE:
        return { text: 'Collection Sale', color: 'bg-pink-100 text-pink-800' };
      case NotificationType.COLLECTION_MINT:
        return { text: 'Minted', color: 'bg-purple-100 text-purple-800' };
      case NotificationType.FOLLOWED_WALLET_BUY:
        return { text: 'Followed Buy', color: 'bg-cyan-100 text-cyan-800' };
      case NotificationType.FOLLOWED_WALLET_SELL:
        return { text: 'Followed Sell', color: 'bg-amber-100 text-amber-800' };
      case NotificationType.LAUNCHPAD_MINT_COMPLETED:
        return { text: 'Mint Ready', color: 'bg-emerald-100 text-emerald-800' };
      case NotificationType.LAUNCHPAD_SETTLEMENT_RECEIVED:
        return { text: 'Settlement', color: 'bg-green-100 text-green-800' };
      case NotificationType.LAUNCHPAD_LAUNCH_COMPLETED:
        return { text: 'Launch Ended', color: 'bg-indigo-100 text-indigo-800' };
      case NotificationType.LAUNCHPAD_MINTER_REVOKED:
        return { text: 'Minter Revoked', color: 'bg-red-100 text-red-800' };
      case NotificationType.COLLECTION_OFFER_CLAIM_AVAILABLE:
        return { text: 'Claim NFT', color: 'bg-emerald-100 text-emerald-800' };
      case NotificationType.REWARDS_CLAIM_PERIOD_OPENED:
        return { text: 'Rewards', color: 'bg-green-100 text-green-800' };
      case NotificationType.AUCTION_OUTBID:
        return { text: 'Outbid', color: 'bg-red-100 text-red-800' };
      case NotificationType.AUCTION_WON:
        return { text: 'Auction Won', color: 'bg-green-100 text-green-800' };
      case NotificationType.AUCTION_SOLD:
        return { text: 'Auction Sold', color: 'bg-purple-100 text-purple-800' };
      case NotificationType.AUCTION_NO_RESERVE_MET:
        return { text: 'Reserve Not Met', color: 'bg-yellow-100 text-yellow-800' };
      default:
        return { text: 'Update', color: 'bg-gray-100 text-gray-800' };
    }
  };

  const getNotificationMessage = (notification: UserNotification): string => {
    const { type, data } = notification;

    switch (type) {
      case NotificationType.NFT_RECEIVED_OFFER:
        return `New offer of ${formatPrice((data as any).offerAmountXrp)} from ${(data as any).offeringWallet?.slice(0, 8) ?? 'Unknown'}...`;
      case NotificationType.NFT_BOUGHT:
        return `You purchased for ${formatPrice((data as any).salePrice)}`;
      case NotificationType.NFT_SOLD:
        return `Sold for ${formatPrice((data as any).salePrice)} to ${(data as any).buyerWallet?.slice(0, 8) ?? 'Unknown'}...`;
      case NotificationType.NFT_OFFER_ACCEPTED:
        return `Your offer of ${formatPrice((data as any).salePrice)} was accepted`;
      case NotificationType.NFT_TRANSFER_OFFER_RECEIVED: {
        const sender = (data as any).offeringWallet?.slice(0, 8) ?? 'Unknown';
        const transferStatus = (data as any).transferStatus;
        if (transferStatus === 'accepted') return `Transfer from ${sender}... — Accepted`;
        if (transferStatus === 'rejected') return `Transfer from ${sender}... — Rejected`;
        return `Transfer offer from ${sender}...`;
      }
      case NotificationType.COLLECTION_NEW_LISTING:
        return `New listing at ${formatPrice((data as any).listingPrice)}`;
      case NotificationType.COLLECTION_NEW_FLOOR: {
        const floorData = data as any;
        const previousFloor = floorData.previousFloorPrice;
        const newFloor = floorData.newFloorPrice;
        const percentDecrease = floorData.floorPriceDecrease;
        const affectedTrait = floorData.affectedTrait;
        const traitInfo = affectedTrait ? ` for ${affectedTrait.traitType}: ${affectedTrait.value}` : '';
        return `Floor dropped from ${formatPrice(previousFloor)} → ${formatPrice(newFloor)} (-${percentDecrease.toFixed(1)}%)${traitInfo}`;
      }
      case NotificationType.COLLECTION_SALE:
        return `Sale at ${formatPrice((data as any).salePrice)}`;
      case NotificationType.COLLECTION_MINT: {
        const mintData = data as any;
        const mintInfo = mintData.mintNumber && mintData.collectionTotalSupply
          ? ` (#${mintData.mintNumber}/${mintData.collectionTotalSupply})`
          : '';
        return `New mint in ${mintData.collectionName || 'collection'}${mintInfo}`;
      }
      case NotificationType.FOLLOWED_WALLET_BUY: {
        const buyData = data as any;
        const wallet = buyData.followedWalletDisplayName || buyData.followedWallet?.slice(0, 8) + '...';
        const price = buyData.salePrice > 0 ? ` for ${formatPrice(buyData.salePrice)}` : '';
        return `${wallet} bought this NFT${price}`;
      }
      case NotificationType.FOLLOWED_WALLET_SELL: {
        const sellData = data as any;
        const wallet = sellData.followedWalletDisplayName || sellData.followedWallet?.slice(0, 8) + '...';
        return `${wallet} sold this NFT for ${formatPrice(sellData.salePrice)}`;
      }
      case NotificationType.LAUNCHPAD_MINT_COMPLETED:
        return `Your mint is ready to claim for ${formatPrice((data as any).mintPriceXrp)}`;
      case NotificationType.LAUNCHPAD_SETTLEMENT_RECEIVED:
        return `Received ${formatPrice((data as any).amountXrp)} settlement for ${(data as any).mintCount} mint${(data as any).mintCount > 1 ? 's' : ''}`;
      case NotificationType.LAUNCHPAD_LAUNCH_COMPLETED:
        return `Your launch "${(data as any).launchName}" has ended (${(data as any).totalMinted}/${(data as any).totalSupply} minted)`;
      case NotificationType.LAUNCHPAD_MINTER_REVOKED: {
        const marketplace = (data as any).newMinterMarketplace;
        const launchName = (data as any).launchName;
        return marketplace
          ? `Your launch "${launchName}" was paused — authorized minter changed to ${marketplace}`
          : `Your launch "${launchName}" was paused — authorized minter was changed or cleared`;
      }
      case NotificationType.COLLECTION_OFFER_CLAIM_AVAILABLE:
        return `Your ${formatPrice((data as any).purchasePrice)} collection offer was accepted - claim your NFT!`;
      case NotificationType.REWARDS_CLAIM_PERIOD_OPENED: {
        const rewardsData = data as any;
        return `${rewardsData.projectName} has opened a rewards claim period`;
      }
      case NotificationType.AUCTION_OUTBID: {
        const d = data as any;
        return `Outbid! New top bid is ${formatPrice(d.newHighestBidXrp)} (your bid was ${formatPrice(d.yourBidXrp)})`;
      }
      case NotificationType.AUCTION_WON: {
        const d = data as any;
        return `You won the auction for ${formatPrice(d.winningBidXrp)}`;
      }
      case NotificationType.AUCTION_SOLD: {
        const d = data as any;
        return `Sold at auction for ${formatPrice(d.winningBidXrp)} — you net ${formatPrice(d.sellerNetXrp)}`;
      }
      case NotificationType.AUCTION_NO_RESERVE_MET: {
        const d = data as any;
        if (d.recipientRole === 'seller') {
          return `Your auction ended without meeting the reserve of ${formatPrice(d.reservePriceXrp)}`;
        }
        return `Auction ended without meeting reserve — cancel your bid from your wallet to free up funds`;
      }
      default:
        return 'Notification';
    }
  };

  // Don't render anything if not authenticated (will redirect)
  if (!isAuthenticated) {
    return null;
  }

  if (error) {
    return (
      <DefaultPageLayout>
        <div className="flex w-full flex-col items-start gap-6">
          <div className="flex w-full flex-col items-start gap-2">
            <span className="text-heading-1 font-heading-1 text-default-font">
              Notifications
            </span>
          </div>
          <div className="w-full bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <svg
                className="h-5 w-5 text-red-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Failed to load notifications
                </h3>
                <p className="mt-2 text-sm text-red-700">
                  {(error as Error).message || 'An unexpected error occurred'}
                </p>
                <button
                  onClick={() => refetch()}
                  className="mt-3 text-sm font-medium text-red-800 hover:text-red-900"
                >
                  Try again
                </button>
              </div>
            </div>
          </div>
        </div>
      </DefaultPageLayout>
    );
  }

  return (
    <DefaultPageLayout>
      <div className="flex w-full flex-col items-start gap-6">
        {/* Page header */}
        <div className="flex w-full flex-col items-start gap-2">
          <span className="text-heading-1 font-heading-1 text-default-font">
            Notifications
          </span>
          <span className="text-body font-body text-subtext-color">
            {totalCount > 0 ? `${totalCount} notification${totalCount !== 1 ? 's' : ''}` : 'No notifications yet'}
          </span>
        </div>

        {/* Loading state for initial load */}
        {isLoading && (
          <div className="flex w-full items-center justify-center py-12" role="status">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
            <span className="ml-3 text-body font-body text-subtext-color">Loading notifications...</span>
          </div>
        )}

        {/* Empty state */}
        {!isLoading && notifications.length === 0 && (
          <div className="flex w-full flex-col items-center justify-center py-12 rounded-md border border-solid border-neutral-border bg-default-background">
            <svg
              className="h-12 w-12 text-subtext-color"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
              />
            </svg>
            <span className="mt-4 text-body-bold font-body-bold text-default-font">No notifications</span>
            <span className="mt-1 text-body font-body text-subtext-color">
              You're all caught up! Check back later for updates.
            </span>
          </div>
        )}

        {/* Notifications table */}
        {!isLoading && notifications.length > 0 && (
          <div className="w-full rounded-md border border-solid border-neutral-border bg-default-background overflow-hidden">
            {/* Desktop table view */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="min-w-full divide-y divide-neutral-border">
                <thead className="bg-neutral-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-caption-bold font-caption-bold text-subtext-color uppercase tracking-wider">
                      NFT
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-caption-bold font-caption-bold text-subtext-color uppercase tracking-wider">
                      Type
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-caption-bold font-caption-bold text-subtext-color uppercase tracking-wider">
                      Details
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-caption-bold font-caption-bold text-subtext-color uppercase tracking-wider">
                      Time
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-default-background divide-y divide-neutral-border">
                  {notifications.map((notification) => {
                    const badge = getNotificationTypeBadge(notification.type);
                    const data = notification.data as any;
                    const isLaunchNotification =
                      notification.type === NotificationType.LAUNCHPAD_SETTLEMENT_RECEIVED ||
                      notification.type === NotificationType.LAUNCHPAD_LAUNCH_COMPLETED ||
                      notification.type === NotificationType.LAUNCHPAD_MINTER_REVOKED;
                    const isRewardsNotification =
                      notification.type === NotificationType.REWARDS_CLAIM_PERIOD_OPENED;
                    const nftName = isRewardsNotification
                      ? data.projectName || 'Project'
                      : isLaunchNotification
                      ? data.launchName || 'Unnamed Launch'
                      : data.nftName || 'Unnamed NFT';
                    const thumbnail = convertImageUrl(
                      isRewardsNotification
                        ? data.projectImageUrl
                        : isLaunchNotification ? data.launchThumbnailUrl : data.nftThumbnailUrl
                    );

                    return (
                      <tr
                        key={notification.id}
                        data-testid="notification-row"
                        className="hover:bg-neutral-50 cursor-pointer transition-colors"
                        onClick={() => handleNotificationClick(notification)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {thumbnail ? (
                              <img
                                src={thumbnail}
                                alt={nftName}
                                className="h-10 w-10 rounded-md object-cover flex-shrink-0"
                              />
                            ) : (
                              <div className="h-10 w-10 rounded-md bg-neutral-100 flex items-center justify-center flex-shrink-0">
                                <svg
                                  className="h-5 w-5 text-subtext-color"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                  />
                                </svg>
                              </div>
                            )}
                            <div className="ml-4">
                              <div className="text-body-bold font-body-bold text-default-font truncate max-w-[200px]">
                                {nftName}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-caption-bold font-caption-bold ${badge.color}`}>
                            {badge.text}
                          </span>
                        </td>
                        <td className="px-6 py-4 min-w-[350px]">
                          <div className="text-body font-body text-default-font whitespace-normal break-words">
                            {getNotificationMessage(notification)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-body font-body text-subtext-color">
                          {formatDistanceToNow(new Date(notification.created_at), {
                            addSuffix: true,
                          })}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile card view */}
            <div className="sm:hidden divide-y divide-neutral-border">
              {notifications.map((notification) => {
                const badge = getNotificationTypeBadge(notification.type);
                const data = notification.data as any;
                const isLaunchNotification =
                  notification.type === NotificationType.LAUNCHPAD_SETTLEMENT_RECEIVED ||
                  notification.type === NotificationType.LAUNCHPAD_LAUNCH_COMPLETED ||
                  notification.type === NotificationType.LAUNCHPAD_MINTER_REVOKED;
                const isRewardsNotification =
                  notification.type === NotificationType.REWARDS_CLAIM_PERIOD_OPENED;
                const nftName = isRewardsNotification
                  ? data.projectName || 'Project'
                  : isLaunchNotification
                  ? data.launchName || 'Unnamed Launch'
                  : data.nftName || 'Unnamed NFT';
                const thumbnail = convertImageUrl(
                  isRewardsNotification
                    ? data.projectImageUrl
                    : isLaunchNotification ? data.launchThumbnailUrl : data.nftThumbnailUrl
                );

                return (
                  <div
                    key={notification.id}
                    data-testid="notification-card"
                    className="p-4 hover:bg-neutral-50 cursor-pointer transition-colors"
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="flex items-start space-x-3">
                      {thumbnail ? (
                        <img
                          src={thumbnail}
                          alt={nftName}
                          className="h-12 w-12 rounded-md object-cover flex-shrink-0"
                        />
                      ) : (
                        <div className="h-12 w-12 rounded-md bg-neutral-100 flex items-center justify-center flex-shrink-0">
                          <svg
                            className="h-6 w-6 text-subtext-color"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                          </svg>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="text-body-bold font-body-bold text-default-font truncate">
                            {nftName}
                          </p>
                          <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-caption-bold font-caption-bold ${badge.color}`}>
                            {badge.text}
                          </span>
                        </div>
                        <p className="mt-1 text-body font-body text-subtext-color">
                          {getNotificationMessage(notification)}
                        </p>
                        <p className="mt-1 text-caption font-caption text-subtext-color">
                          {formatDistanceToNow(new Date(notification.created_at), {
                            addSuffix: true,
                          })}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Infinite scroll sentinel and loading indicator */}
            <div ref={sentinelRef} className="py-4 text-center">
              {isFetchingNextPage && (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-primary"></div>
                  <span className="ml-2 text-body font-body text-subtext-color">Loading more...</span>
                </div>
              )}
              {!hasNextPage && notifications.length > 0 && (
                <p className="text-body font-body text-subtext-color">No more notifications</p>
              )}
            </div>
          </div>
        )}
      </div>
    </DefaultPageLayout>
  );
}
